import { MapPin, Gauge, Navigation, MountainSnow, Cpu, SignalHigh, SignalZero, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { equipmentOf, statusOf } from '../equipment'
import { fmtRelative } from '../i18n/format'
import { statusText } from '../i18n/status'

const fmt = (v, d = 1) => (v != null ? Number(v).toFixed(d) : '—')
const compass = (t, deg) => {
  if (deg == null) return '—'
  const pts = t('common:compass', { returnObjects: true })
  const arr = Array.isArray(pts) ? pts : ['N', 'NE', 'L', 'SE', 'S', 'SO', 'O', 'NO']
  return arr[Math.round(deg / 45) % 8]
}

function Metric({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
      <Icon size={13} className="text-slate-400 dark:text-slate-500 shrink-0" />
      {children}
    </div>
  )
}

export default function DeviceCard({ device, onSelect }) {
  const { t } = useTranslation()
  const { categories, mines } = useWS()
  const status = statusOf(device)
  const equip = equipmentOf(device, categories, mines)
  const online = status.key !== 'down'   // recebendo mensagens (operação ou paragem)
  const lastSeenStr = fmtRelative(device.lastSeen)

  return (
    <button
      onClick={() => onSelect?.(device)}
      className="relative text-left w-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600 transition-all overflow-hidden"
    >
      <span className={`absolute left-0 top-0 bottom-0 w-1 ${status.bar}`} />

      <div className="flex items-start justify-between pl-6 pr-5 pt-5 pb-3">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="flex items-center justify-center w-14 h-14 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0">
            <span className="equip-icon block w-9 h-9 text-slate-500 dark:text-slate-400" style={{ WebkitMaskImage: `url(/equipments/${equip.svg}.svg)`, maskImage: `url(/equipments/${equip.svg}.svg)` }} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 dark:text-slate-100 text-base leading-tight truncate">{equip.name || device.name}</p>
            <p className="text-sm text-slate-400 dark:text-slate-500 truncate">{equip.model ? `${equip.label} · ${equip.model}` : equip.label}</p>
          </div>
        </div>
        <ChevronRight size={18} className="text-slate-300 dark:text-slate-600 shrink-0 mt-1" />
      </div>

      <div className="flex items-center gap-2 pl-6 pr-5 pb-3 text-xs">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${status.soft} ${status.text} font-medium`}>
          <span className="relative flex h-1.5 w-1.5">
            <span className={`absolute inline-flex h-full w-full rounded-full ${status.dot} opacity-75 ${status.pulse ? 'animate-ping' : ''}`} />
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${status.dot}`} />
          </span>
          {statusText(t, status)}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-medium"><Cpu size={11} /> VMC</span>
        <span className={online ? 'text-status-comms' : 'text-status-down'} title={t('ops:devices.comms')}>
          {online ? <SignalHigh size={13} /> : <SignalZero size={13} />}
        </span>
        <span className="ml-auto text-slate-400 dark:text-slate-500">{lastSeenStr}</span>
      </div>

      <div className="pl-6 pr-5 pb-5 grid grid-cols-2 gap-2.5 text-sm border-t border-slate-100 dark:border-slate-800 pt-3.5">
        <Metric icon={MapPin}><span className="truncate tabular-nums text-[13px]">{fmt(device.lastLat, 5)}°&nbsp;{fmt(device.lastLon, 5)}°</span></Metric>
        <Metric icon={MountainSnow}><span className="text-[13px] tabular-nums">{fmt(device.lastAltM)} m</span></Metric>
        <Metric icon={Gauge}><span className="text-[13px] tabular-nums">{fmt(device.lastSpeedKmh)} km/h</span></Metric>
        <Metric icon={Navigation}><span className="text-[13px] tabular-nums">{fmt(device.lastHeadingDeg)}° {compass(t, device.lastHeadingDeg)}</span></Metric>
      </div>
    </button>
  )
}
